import React, { useMemo } from 'react';
import { Edit2, Trash2, UserPlus, Loader } from 'lucide-react';
import type { OrgUnit } from '@schemas/api';
import { useOrgStore } from '@store/orgStore';
import { useListUsers } from '@api/hooks/useUsers';
import { OrgUnitBreadcrumbs } from './OrgUnitBreadcrumbs';

interface OrgUnitDetailPanelProps {
  unit: OrgUnit | null;
  isLoading: boolean;
  canEdit: boolean;
  onEdit: (unitId: string) => void;
  onDelete: (unitId: string) => void;
  onAddMember: (unitId: string) => void;
  onRemoveMember: (unitId: string, memberId: string) => void;
}

export const OrgUnitDetailPanel: React.FC<OrgUnitDetailPanelProps> = ({
  unit,
  isLoading,
  canEdit,
  onEdit,
  onDelete,
  onAddMember,
  onRemoveMember,
}) => {
  const { data: usersData } = useListUsers(0, 1000);
  const userNameMap = useMemo(() => {
    const map = new Map<string, string>();
    if (usersData?.items) {
      for (const user of usersData.items) {
        map.set(user.id, `${user.first_name} ${user.last_name}`);
      }
    }
    return map;
  }, [usersData]);

  if (!unit) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-8 flex items-center justify-center h-full">
        <div className="text-center text-gray-500">
          <p>Select a unit to view details</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden flex flex-col h-full">
      {/* Header */}
      <div className="border-b border-gray-200 p-4 bg-gradient-to-r from-primary-50 to-primary-100">
        <div className="flex items-start justify-between gap-4 mb-2">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">{unit.name}</h2>
            <span className="inline-block text-sm bg-primary-200 text-primary-800 px-2 py-1 rounded mt-1">
              {unit.unit_type}
            </span>
          </div>
          {canEdit && (
            <div className="flex gap-2">
              <button
                onClick={() => onEdit(unit.id)}
                className="p-2 hover:bg-primary-200 rounded transition-colors"
                title="Edit unit"
              >
                <Edit2 size={18} className="text-primary-600" />
              </button>
              <button
                onClick={() => onDelete(unit.id)}
                className="p-2 hover:bg-red-200 rounded transition-colors"
                title="Delete unit"
              >
                <Trash2 size={18} className="text-red-600" />
              </button>
            </div>
          )}
        </div>

        {/* Breadcrumbs */}
        <OrgUnitBreadcrumbs unit={unit} />
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4 space-y-6">
        {/* Description */}
        {unit.description && (
          <div>
            <h3 className="text-sm font-semibold text-gray-700 mb-2">
              Description
            </h3>
            <p className="text-gray-600">{unit.description}</p>
          </div>
        )}

        {/* Unit metadata */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <h3 className="text-sm font-semibold text-gray-700 mb-1">
              Manager
            </h3>
            <p className="text-gray-600">
              {unit.manager_id ? (userNameMap.get(unit.manager_id) || unit.manager_id) : 'Not assigned'}
            </p>
          </div>
          <div>
            <h3 className="text-sm font-semibold text-gray-700 mb-1">
              Path
            </h3>
            <p className="text-xs text-gray-600 truncate">
              {unit.materialized_path}
            </p>
          </div>
          <div>
            <h3 className="text-sm font-semibold text-gray-700 mb-1">
              Created
            </h3>
            <p className="text-gray-600">
              {new Date(unit.created_at).toLocaleDateString()}
            </p>
          </div>
          <div>
            <h3 className="text-sm font-semibold text-gray-700 mb-1">
              Updated
            </h3>
            <p className="text-gray-600">
              {new Date(unit.updated_at).toLocaleDateString()}
            </p>
          </div>
        </div>

        {/* Members section */}
        <div className="border-t border-gray-200 pt-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-gray-700">
              Members ({unit.members?.length || 0})
            </h3>
            {canEdit && (
              <button
                onClick={() => onAddMember(unit.id)}
                className="flex items-center gap-1 px-3 py-1 bg-primary-600 text-white rounded text-sm hover:bg-primary-700 transition-colors"
              >
                <UserPlus size={14} />
                Add Member
              </button>
            )}
          </div>

          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader size={20} className="animate-spin text-gray-400" />
            </div>
          ) : unit.members && unit.members.length > 0 ? (
            <div className="space-y-2">
              {unit.members.map((member) => (
                <div
                  key={member.id}
                  className="flex items-center justify-between p-2 bg-gray-50 rounded hover:bg-gray-100 group"
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900">
                      {userNameMap.get(member.user_id) || member.user_id}
                    </p>
                    <p className="text-xs text-gray-500">
                      {member.membership_type} • Joined{' '}
                      {new Date(member.joined_at).toLocaleDateString()}
                    </p>
                  </div>
                  {canEdit && (
                    <button
                      onClick={() => onRemoveMember(unit.id, member.id)}
                      className="ml-2 px-2 py-1 text-xs text-red-600 hover:bg-red-50 rounded opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      Remove
                    </button>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-500 py-4 text-center">
              No members in this unit
            </p>
          )}
        </div>
      </div>
    </div>
  );
};
